import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { Autocomplete, Box, Chip, TextField, Typography } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import { useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import SvgToIcon from "../../../icons/SvgToIcon";
import { IntegrationsMapping } from "../../stack/integrations/IntegrationsMapping";

const OwnershipsSelect = ({ userDetails, setUserDetails }) => {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const [hoveredChip, setHoveredChip] = useState(null);
  const [inputValue, setInputValue] = useState("");

  const availableIntegrations = Object.keys(IntegrationsMapping)
    .filter(
      (key) =>
        IntegrationsMapping[key].available &&
        !userDetails.ownerships.includes(key)
    )
    .map((key) => ({
      displayName: IntegrationsMapping[key].displayName,
      codeName: IntegrationsMapping[key].codeName,
    }));

  const handleOwnershipChange = (event, newValue) => {
    if (newValue && newValue.length > 0) {
      const lastSelected = newValue[newValue.length - 1];
      if (
        typeof lastSelected !== "string" &&
        !userDetails.ownerships.includes(lastSelected.codeName)
      ) {
        setUserDetails((prevDetails) => ({
          ...prevDetails,
          ownerships: [...prevDetails.ownerships, lastSelected.codeName],
        }));
        trackEvent(
          `User added ownership of integration: ${lastSelected.codeName}`
        );
      }
    }
  };

  return (
    <>
      <Autocomplete
        multiple
        freeSolo
        options={availableIntegrations}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.displayName
        }
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onChange={handleOwnershipChange}
        renderTags={() => null}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Enter tool name"
            variant="standard"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon />
                </InputAdornment>
              ),
              disableUnderline: true,
            }}
            sx={{
              "& .MuiInputBase-input": {
                fontSize: "12px",
              },
              borderBottom: `2px solid ${colors.grey_32percent}`,
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            display="flex"
            alignItems="center"
            gap={1}
          >
            {IntegrationsMapping[option.codeName]?.logo && (
              <SvgToIcon
                IconLogo={IntegrationsMapping[option.codeName]?.logo}
                width={30}
              />
            )}
            <Typography variant="caption" fontWeight={400}>
              {option.displayName}
            </Typography>
          </Box>
        )}
        ListboxProps={{
          style: {
            maxHeight: "140px",
          },
        }}
      />
      <Box display="flex" flexWrap="wrap" gap={1} sx={{ marginTop: "8px" }}>
        {userDetails.ownerships.map((integration, index) => {
          const logoSrc = IntegrationsMapping[integration]?.logo;
          return (
            <Chip
              key={index}
              label={
                <span style={{ marginLeft: -5 }}>
                  {IntegrationsMapping[integration]?.displayName}
                </span>
              }
              onDelete={() => {
                setUserDetails((prevDetails) => ({
                  ...prevDetails,
                  ownerships: prevDetails.ownerships.filter(
                    (item) => item !== integration
                  ),
                }));
              }}
              avatar={
                logoSrc ? <SvgToIcon IconLogo={logoSrc} width={30} /> : null
              }
              sx={{
                backgroundColor: "white",
                color: "black",
                borderRadius: "8px",
                border: `1px solid ${colors.grey_16percent}`,
                "& .MuiChip-deleteIcon": {
                  visibility: hoveredChip === index ? "visible" : "hidden",
                  width: "20px",
                  height: "20px",
                },
              }}
              onMouseEnter={() => setHoveredChip(index)}
              onMouseLeave={() => setHoveredChip(null)}
            />
          );
        })}
      </Box>
    </>
  );
};

export default OwnershipsSelect;
